import React, { useRef, useEffect, useMemo } from 'react';
import LogOut from 'lucide-react/dist/esm/icons/log-out';
import User from 'lucide-react/dist/esm/icons/user';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
import Home from 'lucide-react/dist/esm/icons/home';
import Scale from 'lucide-react/dist/esm/icons/scale';
import Telescope from 'lucide-react/dist/esm/icons/search';
import ClipboardList from 'lucide-react/dist/esm/icons/clipboard-list';
import { LanguageSwitcher } from '../../../components';
import { useUIStore, useOrgStore, useImpersonationStore } from '../../../stores';
import { useDiscoveryStore } from '../../../stores/discoveryStore';
import { getUser } from '../../auth';
import { getTranslation } from '../../../i18n';
import { Permissions } from '../../../config/permissions';
import type { SupportedLanguage } from '../../../types';
import ImpersonationSelector from './ImpersonationSelector';

interface DashboardHeaderProps {
  language: SupportedLanguage;
  results: any;
  showUpload: boolean;
  currentCompanyName: string;
  analysesCount: number;
  onGoToDashboard: () => void;
  onLanguageChange: (lang: SupportedLanguage) => void;
  onLogoutClick: () => void;
  onLogoutConfirm: () => void;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  language,
  results,
  showUpload,
  currentCompanyName,
  analysesCount,
  onGoToDashboard,
  onLanguageChange,
  onLogoutClick,
}) => {
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { userMenuOpen, setUserMenuOpen, setShowComparisonSelector, setDiscoveryPortalOpen, setSurveyPortalOpen } = useUIStore();
  const unreadCount = useDiscoveryStore((s) => s.unreadCount);
  const loadUnreadCount = useDiscoveryStore((s) => s.loadUnreadCount);
  const t = (key: string) => getTranslation(language, key);

  const userContext = useOrgStore((s) => s.userContext);
  const hasDiscoveryAccess = userContext?.permissions?.includes(Permissions.MODULE_DISCOVERY_ACCESS);
  const impersonationActive = useImpersonationStore((s) => s.active);
  const isPlatformAdmin = userContext?.system_role === 'platform_admin';

  useEffect(() => {
    if (hasDiscoveryAccess) {
      loadUnreadCount();
      const interval = setInterval(loadUnreadCount, 60000);
      return () => clearInterval(interval);
    }
  }, [hasDiscoveryAccess, loadUnreadCount]);
  const formattedUsername = useMemo(() => {
    // Prefer display name from user context, fall back to email
    if (userContext?.display_name) return userContext.display_name;
    const username = getUser();
    if (!username) return '';
    return username
      .split(' ')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }, [userContext?.display_name]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setUserMenuOpen]);

  return (
    <header className="px-6 py-1.5 flex justify-between items-center z-dropdown relative border-b border-subtle bg-surface-page animate-fade-in-down flex-shrink-0 h-[60px]">
      <div className="flex items-center gap-4">
        {/* Logo */}
        <div 
          className="flex items-center gap-2.5 cursor-pointer p-1.5 -m-1.5 rounded-lg transition-all duration-200 hover:bg-surface-primary group"
          onClick={onGoToDashboard}
          title="Go to Dashboard"
        >
          <img 
            src="/quazi-logo.svg" 
            alt="QUAZI Logo" 
            className="w-7 h-auto drop-shadow-[0_0_12px_rgba(33,158,188,0.5)] transition-transform duration-200 group-hover:scale-105 logo-glow" 
          />
          <h1 className="font-display text-base font-semibold tracking-[2px] text-content m-0 leading-tight transition-colors duration-200 group-hover:text-primary">
            {t('systemName')}
          </h1>
        </div>
        
        {/* Breadcrumb Navigation */}
        {(results || showUpload) && (
          <nav className="flex items-center gap-1 text-xs">
            <button 
              className="flex items-center gap-1 px-2 py-1 rounded-md text-content-tertiary hover:text-content hover:bg-surface-primary transition-all duration-200 btn-bounce"
              onClick={onGoToDashboard}
            >
              <Home size={12} />
              <span>Dashboard</span>
            </button>
            <ChevronRight size={12} className="text-content-muted" />
            {showUpload ? (
              <span className="px-2 py-1 rounded-md text-primary bg-brand/10">New Analysis</span>
            ) : (
              <span className="px-2 py-1 rounded-md text-status-success bg-status-success/10 truncate max-w-[180px]">
                {currentCompanyName || results?.company_name || 'Analysis'}
              </span>
            )}
          </nav>
        )}
      </div>

      <div className="flex items-center gap-3">
        {/* Discovery Agent Button */}
        {hasDiscoveryAccess && (
          <button
            onClick={() => setDiscoveryPortalOpen(true)}
            className="relative flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 text-amber-300 text-xs font-medium hover:from-amber-500/30 hover:to-orange-500/30 hover:border-amber-500/40 transition-all duration-200"
            title="Discovery Agent"
          >
            <Telescope size={14} />
            <span className="hidden sm:inline">Discovery</span>
            {unreadCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-status-error text-content text-[10px] font-bold leading-none">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
        )}

        {/* Survey Button */}
        <button
          onClick={() => setSurveyPortalOpen(true)}
          className="relative flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 text-emerald-300 text-xs font-medium hover:from-emerald-500/30 hover:to-teal-500/30 hover:border-emerald-500/40 transition-all duration-200"
          title="Surveys"
        >
          <ClipboardList size={14} />
          <span className="hidden sm:inline">Surveys</span>
        </button>

        {/* Compare Button */}
        {analysesCount >= 2 && (
          <button
            onClick={() => setShowComparisonSelector(true)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-violet-500/20 to-cyan-500/20 border border-violet-500/30 text-violet-300 text-xs font-medium hover:from-violet-500/30 hover:to-cyan-500/30 hover:border-violet-500/40 transition-all duration-200"
            title={t('compareAnalysesTitle')}
          >
            <Scale size={14} />
            <span className="hidden sm:inline">{t('compareAnalyses')}</span>
          </button>
        )}
        
        {isPlatformAdmin && !impersonationActive && <ImpersonationSelector />}
        
        <LanguageSwitcher currentLanguage={language} onLanguageChange={onLanguageChange} />
        
        <div className="relative" ref={userMenuRef}>
          <button 
            className="flex items-center gap-2 py-1.5 px-2 border-none rounded-lg bg-transparent text-content text-xs font-medium cursor-pointer transition-all duration-200 hover:bg-surface-primary group"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
          >
            <div className="flex items-center justify-center w-7 h-7 bg-gradient-to-br from-brand to-brand-hover rounded-full text-content-inverse">
              <User size={14} />
            </div>
            <span className="font-medium tracking-wide max-w-[150px] truncate">{formattedUsername}</span>
            <ChevronDown 
              size={16} 
              className={`transition-all duration-250 text-content-tertiary ${userMenuOpen ? 'rotate-180' : ''}`} 
            />
          </button>
          
          {userMenuOpen && (
            <div className="absolute top-[calc(100%+0.25rem)] right-0 min-w-[160px] p-1.5 rounded-lg border border-default shadow-xl z-dropdown animate-fade-in-down bg-surface-page">
              <button 
                className="flex items-center gap-2 w-full py-2 px-3 bg-transparent border-none rounded-md text-content-secondary text-xs font-medium cursor-pointer transition-all duration-150 text-left hover:bg-status-error/10 hover:text-status-error-light"
                onClick={onLogoutClick}
              >
                <LogOut size={14} />
                <span>{t('logout')}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default React.memo(DashboardHeader);
